import { useState, useEffect, useRef, useCallback } from "react";

// ─── DATA ─────────────────────────────────────────────────────────────────────
const DATA = {
  profile: {
    name: "Rushin Makwana",
    title: "Software Engineer & CS Researcher",
    location: "Montreal, QC",
    email: "rushinmakwana@gmail.com",
    phone: "+1 (438) 728-1954",
    linkedin: "https://linkedin.com/in/rushinmakwana",
    github: "https://github.com/Rushin-Makwana",
    portfolio: "https://rushin-portfolio.netlify.app",
    bio: "M.Sc. researcher at Concordia University and full-stack software engineer with 5+ years of experience. I build large-scale systems, design middleware architectures, and publish applied research on software performance and IoT engineering. Twice published at ICSE (IEEE & ACM).",
    skills: ["Java","Python","Spring Boot","REST APIs","React","Angular","Node.js","Docker","Kubernetes","AWS","PostgreSQL","MongoDB","CI/CD","GenAI","Prompt Engineering","R","Git"],
  },
  publications: [
    { id:1, title:"Towards Holistic IoT Systems Profiling: Integrating Energy Consumption with Performance Metrics using SPECTRA", authors:"Makwana R.", venue:"ICSE SERP4IoT Workshop", year:2026, type:"Conference", publisher:"ACM", url:"https://dl.acm.org/doi/10.1145/3786159.3788486", keywords:["energy profiling","IoT","performance metrics","SPECTRA"] },
    { id:2, title:"A Pattern-Driven Middleware Architecture for IoT Systems", authors:"Makwana R. et al.", venue:"ICSE SERP4IoT Workshop", year:2025, type:"Conference", publisher:"IEEE", url:"https://ieeexplore.ieee.org/document/11038518", keywords:["middleware","IoT","architecture","patterns"] },
  ],
  posters: [
    {
      id: 1,
      title: "City Architecture Poster",
      venue: "Voltage 2025 Conference",
      year: 2025, 
      url: "https://drive.google.com/file/d/1Li-RTpdK0rBbxK65d3NUvg_Zkhqqn7UO/view?usp=sharing",
      description: "Poster demontrating the city-scale middleware architecture for collecting, processing, and analysing real-world energy consumption data from various devices across Hydro-Québec's city-scale infrastructure",
    },
    {
      id: 2,
      title: "Middleware Architecture Poster",
      venue: "StartaCom Poster Competition",
      year: 2025,
      url: "https://drive.google.com/file/d/1Li-RTpdK0rBbxK65d3NUvg_Zkhqqn7UO/view?usp=sharing",
      description: "Poster presenting the pattern-driven middleware architecture for data collection, processing, and aggregation and embeds robust security, privacy, and access-control mechanisms",
    },
  ],
  projects: [
    { id:1, title:"SPECTRA", subtitle:"Software Performance & Energy Profiler", desc:"A Java-based automated quality assessment tool that holistically measures energy consumption and runtime performance across large-scale IoT systems. Novel profiling methodology published at ICSE 2026.", stack:["Java","Python","CI/CD","JUnit"], role:"Lead Researcher & Developer", status:"Active", link:"https://github.com/ptidejteam/ptidej-SPECTRA", type:"Research" },
    { id:2, title:"CityData", subtitle:"IoT Middleware for Hydro-Québec", desc:"Middleware solution for collecting, processing, and analysing real-world energy consumption data from distributed IoT devices across Hydro-Québec's city-scale infrastructure.", stack:["Java","REST APIs","MongoDB","Docker"], role:"Researcher & Developer", status:"Active", link:"https://github.com/ptidejteam/tools4cities-CITYdata", type:"Research" },
    { id:3, title:"CityLayer", subtitle:"GeoSpatial Information System", desc:"Proof-of-concept spatial data pipeline for 3D city visualisation, built for the Concordia Next-Gen Cities Institute. Uses statistical inference on large-scale urban datasets.", stack:["Python","R","Java","PostgreSQL"], role:"Developer & Analyst", status:"Active", link:"https://ngci.encs.concordia.ca/citylayers", type:"Research" },
    { id:4, title:"Claims Dashboard", subtitle:"Real-time Processing System", desc:"Full-stack real-time claims management dashboard with .NET Core backend. Reduced processing time by 60% through optimised API architecture and event-driven design.", stack:[".NET Core","REST APIs","Angular","Docker","Kubernetes"], role:"Lead Backend Engineer", status:"Shipped", link:"#", type:"Industry" },
    { id:5, title:"Warzone Engine", subtitle:"Risk Game Engine in Java", desc:"A 4-person collaborative Risk game engine built with Java and JUnit. Implemented CI/CD pipelines, Git branching strategy, and full game logic from scratch.", stack:["Java","JUnit","CI/CD","Git"], role:"Co-developer", status:"Shipped", link:"https://github.com/Concordia-Winter-2024/SOEN-6441-Warzone-Team19", type:"Academic" },
  ],
  experience: [
    { role:"Research Student", org:"Concordia University", period:"Sept 2024 – Present", highlight:"2× ICSE published" },
    { role:"Teaching Assistant", org:"Concordia University", period:"Sept 2024 – Present", highlight:"Software Eng. & DSA" },
    { role:"Director of Dev & IT ", org:"IEEE Concordia (Student Club)", period:"May–Dec 2024", highlight:"+30% engagement" },
    { role:"Software Engineer", org:"TatvaSoft", period:"Apr–Nov 2023", highlight:"60% faster processing" },
    { role:"Junior Software Dev", org:"Aakash Informatics", period:"Feb 2022–Mar 2023", highlight:"10k+ daily users" },
    { role:"Developer Intern", org:"Swastik Laser Tech", period:"Jul 2021–Jan 2022", highlight:"1M+ user records" },
  ],
};

// ─── THEME ────────────────────────────────────────────────────────────────────
const THEMES = {
  dark: {
    bg: "#080C10",
    surface: "#0E1420",
    surfaceAlt: "#131B28",
    border: "#1E2D42",
    accent: "#00E5C8",
    accentDim: "#00E5C820",
    accentHover: "#00FDE0",
    text: "#E8F1FF",
    textMuted: "#6B8299",
    textDim: "#3A5068",
    heading: "#FFFFFF",
    tag: "#0A2030",
    tagText: "#00E5C8",
    cardBg: "#0E1420",
    glow: "0 0 40px #00E5C830",
  },
  light: {
    bg: "#F5F7FA",
    surface: "#FFFFFF",
    surfaceAlt: "#EEF2F7",
    border: "#D4DCE8",
    accent: "#0057CC",
    accentDim: "#0057CC15",
    accentHover: "#0045A8",
    text: "#1A2332",
    textMuted: "#5A6E85",
    textDim: "#A0B0C0",
    heading: "#0A1628",
    tag: "#E8F0FC",
    tagText: "#0057CC",
    cardBg: "#FFFFFF",
    glow: "0 4px 24px #0057CC18",
  },
};

// ─── ICONS ────────────────────────────────────────────────────────────────────
const Icon = ({ name, size = 18 }) => {
  const icons = {
    github: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/></svg>,
    linkedin: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
    mail: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
    external: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15,3 21,3 21,9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
    sun: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>,
    moon: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>,
    download: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7,10 12,15 17,10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
    search: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
    menu: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>,
    close: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
    map: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  };
  return icons[name] || null;
};

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function Portfolio() {
  const [theme, setTheme] = useState("dark");
  const [activeSection, setActiveSection] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pubFilter, setPubFilter] = useState({ query: "", year: "all", type: "all" });
  const [projFilter, setProjFilter] = useState("all");
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [contactErrors, setContactErrors] = useState({});
  const [contactSent, setContactSent] = useState(false);
  const [mounted, setMounted] = useState(false);

  const t = THEMES[theme];

  useEffect(() => { setMounted(true); }, []);

  const navItems = ["home","publications","projects","experience","contact"];

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) { el.scrollIntoView({ behavior: "smooth" }); }
    setMobileMenuOpen(false);
    setActiveSection(id);
  };

  // Intersection observer for active nav
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); });
    }, { threshold: 0.3 });
    navItems.forEach(id => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);

  // Filtered publications
  const filteredPubs = DATA.publications.filter(p => {
    const q = pubFilter.query.toLowerCase();
    const matchQ = !q || p.title.toLowerCase().includes(q) || p.keywords.some(k => k.toLowerCase().includes(q)) || p.venue.toLowerCase().includes(q);
    const matchY = pubFilter.year === "all" || String(p.year) === pubFilter.year;
    const matchT = pubFilter.type === "all" || p.type === pubFilter.type;
    return matchQ && matchY && matchT;
  });

  // Filtered projects
  const filteredProjects = projFilter === "all" ? DATA.projects : DATA.projects.filter(p => p.type === projFilter);

  // Contact form validation
  const validateContact = () => {
    const errs = {};
    if (!contactForm.name.trim()) errs.name = "Name is required";
    if (!contactForm.email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email)) errs.email = "Enter a valid email";
    if (!contactForm.message.trim()) errs.message = "Message is required";
    else if (contactForm.message.trim().length < 20) errs.message = "Message too short (min 20 chars)";
    return errs;
  };

  const handleContactSubmit = () => {
    const errs = validateContact();
    if (Object.keys(errs).length) { setContactErrors(errs); return; }
    setContactErrors({});
    setContactSent(true);
    setTimeout(() => setContactSent(false), 5000);
    setContactForm({ name: "", email: "", message: "" });
  };

  const pubYears = [...new Set(DATA.publications.map(p => p.year))].sort((a,b) => b-a);
  const pubTypes = [...new Set(DATA.publications.map(p => p.type))];
  const projTypes = [...new Set(DATA.projects.map(p => p.type))];

  const statusColor = (s) => s === "Active" ? "#00E5C8" : s === "Shipped" ? "#6C8EFF" : "#F0A500";

  if (!mounted) return null;

  return (
    <div style={{ fontFamily: "'DM Sans', 'Sora', system-ui, sans-serif", background: t.bg, color: t.text, minHeight: "100vh", transition: "background 0.3s, color 0.3s" }}>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,400&family=DM+Mono:wght@400;500&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: ${t.bg}; }
        ::-webkit-scrollbar-thumb { background: ${t.border}; border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: ${t.accent}; }

        .nav-link { cursor: pointer; transition: color 0.2s; }
        .nav-link:hover { color: ${t.accent}; }
        .nav-link.active { color: ${t.accent}; }

        .btn-primary {
          background: ${t.accent}; color: ${t.bg}; border: none; cursor: pointer;
          padding: 10px 22px; border-radius: 6px; font-weight: 600; font-size: 14px;
          transition: all 0.2s; font-family: inherit;
          display: inline-flex; align-items: center; gap: 7px;
        }
        .btn-primary:hover { background: ${t.accentHover}; transform: translateY(-1px); box-shadow: ${t.glow}; }

        .btn-ghost {
          background: transparent; color: ${t.text}; border: 1px solid ${t.border}; cursor: pointer;
          padding: 10px 22px; border-radius: 6px; font-weight: 500; font-size: 14px;
          transition: all 0.2s; font-family: inherit;
          display: inline-flex; align-items: center; gap: 7px;
        }
        .btn-ghost:hover { border-color: ${t.accent}; color: ${t.accent}; }

        .card {
          background: ${t.cardBg}; border: 1px solid ${t.border}; border-radius: 12px;
          padding: 24px; transition: all 0.25s;
        }
        .card:hover { border-color: ${t.accent}40; transform: translateY(-2px); box-shadow: ${t.glow}; }

        .tag {
          background: ${t.tag}; color: ${t.tagText}; border-radius: 4px;
          padding: 3px 10px; font-size: 12px; font-weight: 500; font-family: 'DM Mono', monospace;
          display: inline-block; white-space: nowrap;
        }

        .skill-tag {
          background: ${t.surfaceAlt}; color: ${t.textMuted}; border: 1px solid ${t.border};
          border-radius: 20px; padding: 5px 13px; font-size: 13px; font-weight: 500;
          transition: all 0.2s; cursor: default;
        }
        .skill-tag:hover { border-color: ${t.accent}; color: ${t.accent}; background: ${t.accentDim}; }

        .filter-btn {
          background: ${t.surfaceAlt}; color: ${t.textMuted}; border: 1px solid ${t.border};
          border-radius: 20px; padding: 6px 16px; font-size: 13px; font-weight: 500;
          cursor: pointer; transition: all 0.2s; font-family: inherit;
        }
        .filter-btn:hover, .filter-btn.active {
          background: ${t.accentDim}; color: ${t.accent}; border-color: ${t.accent}40;
        }

        .input-field {
          background: ${t.surfaceAlt}; border: 1px solid ${t.border}; color: ${t.text};
          border-radius: 8px; padding: 11px 15px; font-size: 14px; width: 100%;
          outline: none; transition: border-color 0.2s; font-family: inherit;
        }
        .input-field:focus { border-color: ${t.accent}; }
        .input-field::placeholder { color: ${t.textDim}; }
        .input-field.error { border-color: #FF5A5A; }

        .section-label {
          font-family: 'DM Mono', monospace; font-size: 11px; font-weight: 500;
          letter-spacing: 0.15em; text-transform: uppercase; color: ${t.accent};
          margin-bottom: 6px;
        }

        .hero-name {
          font-family: 'Sora', sans-serif; font-weight: 800;
          font-size: clamp(44px, 8vw, 88px); line-height: 1.0;
          color: ${t.heading}; letter-spacing: -0.03em;
        }

        .section-title {
          font-family: 'Sora', sans-serif; font-weight: 700;
          font-size: clamp(28px, 5vw, 42px); color: ${t.heading};
          letter-spacing: -0.02em; line-height: 1.2;
        }

        .pub-card { cursor: pointer; }
        .pub-card:hover .pub-title { color: ${t.accent}; }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fade-up { animation: fadeUp 0.6s ease both; }
        .fade-up-1 { animation-delay: 0.1s; }
        .fade-up-2 { animation-delay: 0.2s; }
        .fade-up-3 { animation-delay: 0.3s; }
        .fade-up-4 { animation-delay: 0.4s; }
        .fade-up-5 { animation-delay: 0.5s; }

        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.6); }
        }
        .live-dot { animation: pulse-dot 2s ease-in-out infinite; }

        .grid-2 { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 16px; }
        .grid-3 { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px; }

        @media (max-width: 768px) {
          .hide-mobile { display: none !important; }
          .show-mobile { display: flex !important; }
        }
        @media (min-width: 769px) {
          .show-mobile { display: none !important; }
        }

        textarea.input-field { resize: vertical; min-height: 120px; }

        .accent-line {
          width: 40px; height: 3px; background: ${t.accent}; border-radius: 2px; margin-bottom: 16px;
        }

        .timeline-item::before {
          content: '';
          position: absolute; left: -25px; top: 6px;
          width: 10px; height: 10px; border-radius: 50%;
          background: ${t.accent}; box-shadow: 0 0 0 3px ${t.accentDim};
        }
        .timeline-line {
          position: absolute; left: -21px; top: 16px; bottom: -20px;
          width: 2px; background: ${t.border};
        }
      `}</style>

      {/* ── NAVBAR ─────────────────────────────────────────────────────────── */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        background: `${t.bg}E8`, backdropFilter: "blur(16px)",
        borderBottom: `1px solid ${t.border}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 clamp(20px, 5vw, 60px)", height: 60,
      }}>
        <div onClick={() => scrollTo("home")} style={{
          fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: 18,
          color: t.heading, cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ color: t.accent }}>R</span>ushin<span style={{ color: t.textMuted, fontWeight: 400 }}>.dev</span>
        </div>

        {/* Desktop nav */}
        <div className="hide-mobile" style={{ display: "flex", alignItems: "center", gap: 32 }}>
          {navItems.map(id => (
            <span key={id} className={`nav-link ${activeSection === id ? "active" : ""}`}
              onClick={() => scrollTo(id)}
              style={{ fontSize: 14, fontWeight: 500, color: activeSection === id ? t.accent : t.textMuted, textTransform: "capitalize" }}>
              {id}
            </span>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => setTheme(t => t === "dark" ? "light" : "dark")}
            className="btn-ghost" style={{ padding: "7px 12px", borderRadius: 8 }}>
            {theme === "dark" ? <Icon name="sun" size={16} /> : <Icon name="moon" size={16} />}
          </button>
          <button className="show-mobile btn-ghost" style={{ padding: "7px 12px", borderRadius: 8 }}
            onClick={() => setMobileMenuOpen(o => !o)}>
            <Icon name={mobileMenuOpen ? "close" : "menu"} size={18} />
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div style={{
          position: "fixed", top: 60, left: 0, right: 0, zIndex: 99,
          background: t.surface, borderBottom: `1px solid ${t.border}`,
          padding: "16px 24px", display: "flex", flexDirection: "column", gap: 4,
        }}>
          {navItems.map(id => (
            <span key={id} onClick={() => scrollTo(id)} style={{
              padding: "12px 0", fontSize: 16, fontWeight: 500, cursor: "pointer",
              color: activeSection === id ? t.accent : t.text,
              borderBottom: `1px solid ${t.border}`,
            }}>{id.charAt(0).toUpperCase() + id.slice(1)}</span>
          ))}
        </div>
      )}

      {/* ── HERO ───────────────────────────────────────────────────────────── */}
      <section id="home" style={{ minHeight: "100vh", display: "flex", alignItems: "center", padding: "100px clamp(20px, 8vw, 120px) 60px", position: "relative", overflow: "hidden" }}>

        {/* BG grid decoration */}
        <div style={{ position: "absolute", inset: 0, backgroundImage: `linear-gradient(${t.border}22 1px, transparent 1px), linear-gradient(90deg, ${t.border}22 1px, transparent 1px)`, backgroundSize: "60px 60px", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: "20%", right: "-10%", width: "50vw", height: "50vw", maxWidth: 600, maxHeight: 600, borderRadius: "50%", background: `radial-gradient(circle, ${t.accent}0A 0%, transparent 70%)`, pointerEvents: "none" }} />

        <div style={{ maxWidth: 900, position: "relative" }}>
          <div className="fade-up" style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
            <div className="live-dot" style={{ width: 8, height: 8, borderRadius: "50%", background: t.accent }} />
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: t.accent, letterSpacing: "0.12em", textTransform: "uppercase" }}>
              Available for Research & Software Engineering Role · Montreal, QC
            </span>
          </div>

          <h1 className="hero-name fade-up fade-up-1">
            {DATA.profile.name.split(" ").map((w, i) => (
              <span key={i} style={{ display: "block" }}>
                {i === 1 ? <><span style={{ color: t.accent }}>{w.charAt(0)}</span>{w.slice(1)}</> : w}
              </span>
            ))}
          </h1>

          <p className="fade-up fade-up-2" style={{ fontSize: "clamp(16px, 2.2vw, 20px)", color: t.textMuted, maxWidth: 600, lineHeight: 1.7, marginTop: 20, marginBottom: 28, fontWeight: 300 }}>
            {DATA.profile.bio}
          </p>

          <div className="fade-up fade-up-3" style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 36 }}>
            <button className="btn-primary" onClick={() => scrollTo("publications")}>
              <Icon name="external" size={15} /> View Publications
            </button>
            <button className="btn-ghost" onClick={() => scrollTo("projects")}>
              Explore Projects
            </button>
            <a href="mailto:rushinmakwana@gmail.com" className="btn-ghost" style={{ textDecoration: "none" }}>
              <Icon name="mail" size={15} /> Get in Touch
            </a>
          </div>

          {/* Stats */}
          <div className="fade-up fade-up-4" style={{ display: "flex", flexWrap: "wrap", gap: 28, marginBottom: 40 }}>
            {[["2×","ICSE Published"],["5+","Yrs Engineering"],["3","Active Research Projects"],["1M+","Users Impacted"]].map(([num, label]) => (
              <div key={label}>
                <div style={{ fontFamily: "'Sora', sans-serif", fontSize: 28, fontWeight: 800, color: t.heading, lineHeight: 1 }}>{num}</div>
                <div style={{ fontSize: 12, color: t.textMuted, marginTop: 4, fontFamily: "'DM Mono', monospace" }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Skills */}
          <div className="fade-up fade-up-5" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {DATA.profile.skills.map(s => <span key={s} className="skill-tag">{s}</span>)}
          </div>
        </div>

        {/* Social sidebar */}
        <div className="hide-mobile" style={{
          position: "fixed", left: 24, bottom: 40, display: "flex", flexDirection: "column",
          alignItems: "center", gap: 16, zIndex: 50,
        }}>
          {[["github",DATA.profile.github],["linkedin",DATA.profile.linkedin],["mail",`mailto:${DATA.profile.email}`]].map(([name, href]) => (
            <a key={name} href={href} target="_blank" rel="noopener noreferrer"
              style={{ color: t.textMuted, transition: "color 0.2s" }}
              onMouseOver={e => e.currentTarget.style.color = t.accent}
              onMouseOut={e => e.currentTarget.style.color = t.textMuted}>
              <Icon name={name} size={18} />
            </a>
          ))}
          <div style={{ width: 1, height: 50, background: t.border }} />
        </div>
      </section>

      {/* ── PUBLICATIONS ───────────────────────────────────────────────────── */}
      <section id="publications" style={{ padding: "80px clamp(20px, 8vw, 120px)", background: t.surface }}>
        <div style={{ maxWidth: 900 }}>
          <p className="section-label">Research output</p>
          <h2 className="section-title" style={{ marginBottom: 8 }}>Publications</h2>
          <p style={{ color: t.textMuted, fontSize: 15, marginBottom: 32, lineHeight: 1.7 }}>
            Peer-reviewed work published at <strong style={{ color: t.text }}>ICSE SERP4IoT</strong> — the premier IoT software engineering workshop.
          </p>

          {/* Filters */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 24, alignItems: "center" }}>
            <div style={{ position: "relative", flex: "1 1 200px" }}>
              <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: t.textMuted }}>
                <Icon name="search" size={15} />
              </span>
              <input className="input-field" placeholder="Search publications..." value={pubFilter.query}
                onChange={e => setPubFilter(f => ({ ...f, query: e.target.value }))}
                style={{ paddingLeft: 38 }} />
            </div>
            <select className="input-field" value={pubFilter.year}
              onChange={e => setPubFilter(f => ({ ...f, year: e.target.value }))}
              style={{ flex: "0 1 120px" }}>
              <option value="all">All Years</option>
              {pubYears.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select className="input-field" value={pubFilter.type}
              onChange={e => setPubFilter(f => ({ ...f, type: e.target.value }))}
              style={{ flex: "0 1 150px" }}>
              <option value="all">All Types</option>
              {pubTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {/* Publication cards */}
          {filteredPubs.length === 0 ? (
            <div style={{ textAlign: "center", padding: "48px 0", color: t.textMuted }}>No publications match your filters.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {filteredPubs.map(pub => (
                <a key={pub.id} href={pub.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                  <div className="card pub-card">
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12, alignItems: "center" }}>
                      <span className="tag" style={{ background: pub.publisher === "IEEE" ? "#003A8C22" : "#8B000022", color: pub.publisher === "IEEE" ? "#4A9EFF" : "#FF6B6B" }}>
                        {pub.publisher}
                      </span>
                      <span className="tag">{pub.type}</span>
                      <span className="tag">{pub.year}</span>
                      <span style={{ marginLeft: "auto", color: t.accent }}><Icon name="external" size={14} /></span>
                    </div>
                    <h3 className="pub-title" style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: "clamp(14px, 2vw, 17px)", color: t.heading, lineHeight: 1.5, marginBottom: 8, transition: "color 0.2s" }}>
                      {pub.title}
                    </h3>
                    <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 12 }}>{pub.authors} · {pub.venue}, {pub.year}</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {pub.keywords.map(k => <span key={k} className="tag">{k}</span>)}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}

            {/* Posters */}
            <div style={{ marginTop: 44 }}>
              <h3 style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: 22, color: t.heading, marginBottom: 24 }}>
              Research Posters
              </h3>

              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {DATA.posters.map(poster => (
                  <a key={poster.id} href={poster.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                    <div className="card pub-card">
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12, alignItems: "center" }}>
                        <span className="tag" style={{ background: t.accentDim, color: t.accent }}>
                          Poster
                        </span>
                        <span className="tag">{poster.year}</span>
                        <span className="tag">{poster.venue}</span>
                        <span style={{ marginLeft: "auto", color: t.accent }}><Icon name="external" size={14} /></span>
                      </div>
                      <h3 className="pub-title" style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: "clamp(14px, 2vw, 17px)", color: t.heading, lineHeight: 1.5, marginBottom: 8, transition: "color 0.2s" }}>
                        {poster.title}
                      </h3>
                      <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 8 }}>
                        {poster.venue}, {poster.year}
                      </p>
                      <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 0 }}>
                        {poster.description}
                      </p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
        </div>
      </section>

      {/* ── PROJECTS ───────────────────────────────────────────────────────── */}
      <section id="projects" style={{ padding: "80px clamp(20px, 8vw, 120px)" }}>
        <div style={{ maxWidth: 1080 }}>
          <p className="section-label">What I've built</p>
          <h2 className="section-title" style={{ marginBottom: 8 }}>Projects</h2>
          <p style={{ color: t.textMuted, fontSize: 15, marginBottom: 28, lineHeight: 1.7 }}>
            From published research tools to production systems at scale.
          </p>

          {/* Project type filter */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 28 }}>
            {["all", ...projTypes].map(type => (
              <button key={type} className={`filter-btn ${projFilter === type ? "active" : ""}`}
                onClick={() => setProjFilter(type)}>
                {type === "all" ? "All Projects" : type}
              </button>
            ))}
          </div>

          <div className="grid-3">
            {filteredProjects.map((proj, i) => (
              <div key={proj.id} className="card" style={{ display: "flex", flexDirection: "column", animationDelay: `${i * 0.05}s` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                  <span className="tag" style={{ background: proj.type === "Research" ? t.accentDim : t.surfaceAlt, color: proj.type === "Research" ? t.accent : t.textMuted }}>
                    {proj.type}
                  </span>
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <div className={proj.status === "Active" ? "live-dot" : ""} style={{ width: 7, height: 7, borderRadius: "50%", background: statusColor(proj.status) }} />
                    <span style={{ fontSize: 11, color: statusColor(proj.status), fontFamily: "'DM Mono', monospace" }}>{proj.status}</span>
                  </div>
                </div>

                <h3 style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: 20, color: t.heading, marginBottom: 4 }}>{proj.title}</h3>
                <p style={{ fontSize: 12, color: t.accent, fontFamily: "'DM Mono', monospace", marginBottom: 12 }}>{proj.subtitle}</p>
                <p style={{ fontSize: 14, color: t.textMuted, lineHeight: 1.7, marginBottom: 16, flex: 1 }}>{proj.desc}</p>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
                  {proj.stack.map(s => <span key={s} className="tag" style={{ fontSize: 11 }}>{s}</span>)}
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: t.textMuted }}>{proj.role}</span>
                  {proj.link !== "#" && (
                    <a href={proj.link} target="_blank" rel="noopener noreferrer"
                      style={{ color: t.accent, display: "flex", alignItems: "center", gap: 5, fontSize: 13, fontWeight: 500, textDecoration: "none" }}>
                      <Icon name="github" size={14} /> View
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── EXPERIENCE ─────────────────────────────────────────────────────── */}
      <section id="experience" style={{ padding: "80px clamp(20px, 8vw, 120px)", background: t.surface }}>
        <div style={{ maxWidth: 760 }}>
          <p className="section-label">Career path</p>
          <h2 className="section-title" style={{ marginBottom: 40 }}>Experience</h2>

          <div style={{ position: "relative", paddingLeft: 32 }}>
            {DATA.experience.map((exp, i) => (
              <div key={i} className="timeline-item" style={{ position: "relative", marginBottom: i < DATA.experience.length - 1 ? 36 : 0 }}>
                {i < DATA.experience.length - 1 && <div className="timeline-line" />}
                <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <div>
                    <h3 style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 17, color: t.heading, marginBottom: 3 }}>{exp.role}</h3>
                    <p style={{ fontSize: 14, color: t.textMuted }}>{exp.org}</p>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: t.textMuted }}>{exp.period}</span>
                    <div><span className="tag" style={{ marginTop: 6 }}>{exp.highlight}</span></div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 48, display: "flex", flexWrap: "wrap", gap: 12 }}>
            <a href={DATA.profile.linkedin} target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ textDecoration: "none" }}>
              <Icon name="linkedin" size={15} /> Full Profile on LinkedIn
            </a>
           <a 
              href="/Rushin_Resume.pdf" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn-ghost" 
              style={{ textDecoration: "none" }}
            >
              <Icon name="download" size={15} /> Download CV (PDF)
            </a>
          </div>
        </div>
      </section>

      {/* ── CONTACT ────────────────────────────────────────────────────────── */}
      <section id="contact" style={{ padding: "80px clamp(20px, 8vw, 120px)" }}>
        <div style={{ maxWidth: 600 }}>
          <p className="section-label">Let's connect</p>
          <h2 className="section-title" style={{ marginBottom: 8 }}>Get in Touch</h2>
          <p style={{ color: t.textMuted, fontSize: 15, lineHeight: 1.7, marginBottom: 36 }}>
            Open to research collaborations, engineering roles, and interesting problems. I'll get back to you within 24 hours.
          </p>

          {contactSent && (
            <div style={{ background: `${t.accent}18`, border: `1px solid ${t.accent}40`, borderRadius: 8, padding: "14px 18px", marginBottom: 20, color: t.accent, fontSize: 14 }}>
              ✓ Message sent! I'll be in touch soon.
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <label style={{ fontSize: 13, color: t.textMuted, display: "block", marginBottom: 6, fontWeight: 500 }}>Name</label>
              <input className={`input-field ${contactErrors.name ? "error" : ""}`} placeholder="Your name" value={contactForm.name}
                onChange={e => setContactForm(f => ({ ...f, name: e.target.value }))} />
              {contactErrors.name && <p style={{ color: "#FF5A5A", fontSize: 12, marginTop: 4 }}>{contactErrors.name}</p>}
            </div>
            <div>
              <label style={{ fontSize: 13, color: t.textMuted, display: "block", marginBottom: 6, fontWeight: 500 }}>Email</label>
              <input className={`input-field ${contactErrors.email ? "error" : ""}`} type="email" placeholder="your@email.com" value={contactForm.email}
                onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))} />
              {contactErrors.email && <p style={{ color: "#FF5A5A", fontSize: 12, marginTop: 4 }}>{contactErrors.email}</p>}
            </div>
            <div>
              <label style={{ fontSize: 13, color: t.textMuted, display: "block", marginBottom: 6, fontWeight: 500 }}>Message</label>
              <textarea className={`input-field ${contactErrors.message ? "error" : ""}`} placeholder="Tell me what you're working on..." value={contactForm.message}
                onChange={e => setContactForm(f => ({ ...f, message: e.target.value }))} />
              {contactErrors.message && <p style={{ color: "#FF5A5A", fontSize: 12, marginTop: 4 }}>{contactErrors.message}</p>}
            </div>
            <button className="btn-primary" onClick={handleContactSubmit} style={{ alignSelf: "flex-start" }}>
              <Icon name="mail" size={15} /> Send Message
            </button>
          </div>

          <div style={{ marginTop: 48, paddingTop: 32, borderTop: `1px solid ${t.border}`, display: "flex", flexWrap: "wrap", gap: 20 }}>
            {[[<Icon name="mail" size={15}/>, DATA.profile.email, `mailto:${DATA.profile.email}`],
              [<Icon name="map" size={15}/>, DATA.profile.location, null],
              [<Icon name="github" size={15}/>, "GitHub", DATA.profile.github],
              [<Icon name="linkedin" size={15}/>, "LinkedIn", DATA.profile.linkedin],
            ].map(([icon, label, href], i) => (
              href ? (
                <a key={i} href={href} target="_blank" rel="noopener noreferrer"
                  style={{ display: "flex", alignItems: "center", gap: 8, color: t.textMuted, fontSize: 14, textDecoration: "none", transition: "color 0.2s" }}
                  onMouseOver={e => e.currentTarget.style.color = t.accent}
                  onMouseOut={e => e.currentTarget.style.color = t.textMuted}>
                  {icon} {label}
                </a>
              ) : (
                <span key={i} style={{ display: "flex", alignItems: "center", gap: 8, color: t.textMuted, fontSize: 14 }}>
                  {icon} {label}
                </span>
              )
            ))}
          </div>
        </div>
      </section>

      {/* ── FOOTER ─────────────────────────────────────────────────────────── */}
      <footer style={{ padding: "24px clamp(20px, 8vw, 120px)", borderTop: `1px solid ${t.border}`, display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: t.textDim }}>
          © 2026 Rushin Makwana
        </span>
        <div style={{ display: "flex", gap: 16 }}>
          {navItems.map(id => (
            <span key={id} onClick={() => scrollTo(id)}
              style={{ fontSize: 12, color: t.textDim, cursor: "pointer", textTransform: "capitalize", transition: "color 0.2s" }}
              onMouseOver={e => e.currentTarget.style.color = t.accent}
              onMouseOut={e => e.currentTarget.style.color = t.textDim}>
              {id}
            </span>
          ))}
        </div>
      </footer>
    </div>
  );
}